import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { openCookiePreferences } from "@/components/CookieConsent";
import { Linkedin, Instagram, Facebook, Phone, Mail, MapPin } from "lucide-react";
import { api } from "@/lib/api";

// URLs are managed in Admin → Settings (GET /api/site-settings/social); empty = icon hidden.
const SOCIALS = [
  { key: "facebook", label: "Facebook", Icon: Facebook },
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "linkedin", label: "LinkedIn", Icon: Linkedin },
];

export default function Footer() {
  const [social, setSocial] = useState(null);
  const [contact, setContact] = useState(null);
  useEffect(() => {
    api.get("/site-settings/footer").then(({ data }) => { setSocial(data.social || {}); setContact(data.contact || {}); }).catch(() => { setSocial({}); setContact({}); });
  }, []);
  return (
    <footer className="mt-24 border-t border-[#E8ECE9] bg-[#F0EFEA]" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-6 gap-8">
        <div className="col-span-2">
          <div className="font-serif text-xl text-[#11211A]">Accommodation Suppliers</div>
          <p className="text-sm text-[#4A5550] mt-3 max-w-md leading-relaxed">
            Hospitality Procurement Marketplace. Source hotel essentials. Get quotes fast — from verified suppliers across linen, textiles and (soon) more categories.
          </p>
          {contact && (contact.phone || contact.email || contact.address) && (
            <div className="mt-5 space-y-1.5 text-sm text-[#4A5550]" data-testid="footer-contact">
              {contact.address && (
                <div className="flex items-start gap-2" data-testid="footer-address"><MapPin className="w-3.5 h-3.5 text-[#264839] mt-1 shrink-0" /><span className="whitespace-pre-line">{contact.address}</span></div>
              )}
              {contact.phone && (
                <a href={`tel:${contact.phone.replace(/[^+\d]/g, "")}`} className="flex items-center gap-2 hover:text-[#264839]" data-testid="footer-phone"><Phone className="w-3.5 h-3.5 text-[#264839]" /> {contact.phone}</a>
              )}
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="flex items-center gap-2 hover:text-[#264839] break-all" data-testid="footer-email"><Mail className="w-3.5 h-3.5 text-[#264839]" /> {contact.email}</a>
              )}
            </div>
          )}
          {social && SOCIALS.some((s) => social[s.key]) && (
            <div className="mt-5 flex items-center gap-3" data-testid="footer-social">
              {SOCIALS.filter((s) => social[s.key]).map(({ key, label, Icon }) => (
                <a
                  key={key}
                  href={social[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Accommodation Suppliers on ${label}`}
                  className="w-9 h-9 flex items-center justify-center border border-[#C4CFC9] text-[#264839] hover:bg-[#264839] hover:text-white transition-colors"
                  data-testid={`footer-${key}`}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="tracking-overline mb-3">Browse</div>
          <ul className="space-y-2 text-sm text-[#4A5550]">
            <li><Link to="/for-hotels" className="hover:text-[#264839]" data-testid="footer-for-hotels">For Hotels</Link></li>
            <li><Link to="/categories" className="hover:text-[#264839]">All categories</Link></li>
            <li><Link to="/search?featured=true" className="hover:text-[#264839]">Featured suppliers</Link></li>
            <li><Link to="/search" className="hover:text-[#264839]">Advanced search</Link></li>
          </ul>
        </div>
        <div>
          <div className="tracking-overline mb-3">For Suppliers</div>
          <ul className="space-y-2 text-sm text-[#4A5550]">
            <li><Link to="/pricing" className="hover:text-[#264839]">Pricing & plans</Link></li>
            <li><Link to="/register" className="hover:text-[#264839]">Create account</Link></li>
            <li><Link to="/login" className="hover:text-[#264839]">Sign in</Link></li>
          </ul>
        </div>
        <div>
          <div className="tracking-overline mb-3">Resources</div>
          <ul className="space-y-2 text-sm text-[#4A5550]">
            <li>
              <Link to="/resources/linen-buyers-playbook" className="hover:text-[#264839]" data-testid="footer-playbook-link">
                The Linen Buyer's Playbook
              </Link>
            </li>
            <li>
              <Link to="/resources/market-benchmarks" className="hover:text-[#264839]" data-testid="footer-benchmarks-link">
                Market Benchmarks
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <div className="tracking-overline mb-3">Legal</div>
          <ul className="space-y-2 text-sm text-[#4A5550]">
            <li>
              <Link to="/terms" className="hover:text-[#264839]" data-testid="footer-terms-link">
                Terms of Use
              </Link>
            </li>
            <li>
              <Link to="/privacy" className="hover:text-[#264839]" data-testid="footer-privacy-link">
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link to="/payments-refunds" className="hover:text-[#264839]" data-testid="footer-payments-link">
                Payments, Refunds &amp; Disclaimers
              </Link>
            </li>
            <li>
              <Link to="/contact" className="hover:text-[#264839]" data-testid="footer-contact-link">
                Contact us
              </Link>
            </li>
            <li>
              <button
                type="button"
                onClick={openCookiePreferences}
                className="hover:text-[#264839] text-left"
                data-testid="footer-cookie-preferences"
              >
                Cookie preferences
              </button>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[#E8ECE9] py-5 text-center text-xs text-[#7D8C85]">
        © {new Date().getFullYear()} QuArete Pty Ltd, trading as Accommodation Suppliers. All rights reserved.
      </div>
    </footer>
  );
}
